import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Tooltip } from 'primereact/tooltip';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { useDispatch, useSelector } from 'react-redux';
import { createPaymentRecord, getTrustAccountBalance, getInvoice } from 'services/billingServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { dollarFormat } from 'utils/utils';
import { totalCountEvent } from 'services/generalServices';
import { listAccounts } from 'redux/actions/AccountListAction';
import { UserDetailsContext } from 'context/userDetailsContext';
import { ModalHeader } from 'shared/ModalHeader';
import { paymentMethods, paymentStructure } from 'constants/dropdownOptions';
import { useCaseAndLeadsList } from 'hooks/useCaseAndLeadList';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'modules/BillingV2/config/queryClient';
import { isMappedViaCaseDefault } from 'modules/BillingV2/utils/depositAccountLockUtils';
import { RecordPaymentEarnedAllocationFields } from 'modules/BillingV2/components/RecordPaymentEarnedAllocationFields/RecordPaymentEarnedAllocationFields';
import { invalidateClientFlatFeeBillingQueries } from 'modules/client/FlatFeeBillingDashboard/hooks/queries/useFlatFeeBillingDashboardQuery';

const typeOfPayments = [
  { label: 'Offline', value: 'offline' },
  { label: 'From trust account', value: 'trust' },
];

const isFlatFeeStructure = (value) => value?.toLowerCase() === paymentStructure.FLAT_FEE.toLowerCase();

const toCents = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(numericValue)) {
    return null;
  }
  return Math.round(numericValue * 100);
};

const allocationAmountCents = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  return toCents(value) ?? 0;
};

export const RecordPaymentV2 = (props) => {
  const { isLeadSpecific = false } = props;
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [typeOfPayment, setTypeOfPayment] = useState('offline');
  const [amount, setAmount] = useState();
  const [recordDate, setRecordDate] = useState(new Date());
  const [payFull, setPayFull] = useState(false);
  const [description, setDescription] = useState();
  const [formErrors, setFormErrors] = useState({
    amount: '',
    record_date: '',
    payment_method: '',
    deposit_info: '',
    trust_account: '',
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  const accountsList = useSelector((state) => state.accountsList.accounts.account);
  const [selectedAccount, setSelectedAccount] = useState();
  const [loading, setLoading] = useState(false);
  const [trustAccountBalance, setTrustAccountBalance] = useState();
  const [selectedTrustAccount, setSelectedTrustAccount] = useState();
  const [invoiceDetails, setInvoiceDetails] = useState();
  const [markAsEarnedChecked, setMarkAsEarnedChecked] = useState(false);
  const [transferToTrustAmount, setTransferToTrustAmount] = useState(null);
  const [temporaryFundAmount, setTemporaryFundAmount] = useState(null);
  const [markAsEarnedAmount, setMarkAsEarnedAmount] = useState(null);
  const [allocationTrustAccount, setAllocationTrustAccount] = useState();
  const [allocationErrors, setAllocationErrors] = useState({});
  const userContext = useContext(UserDetailsContext);
  const fetchedInvoiceSkRef = useRef(null);

  const { caseAndLeadsList } = useCaseAndLeadsList(props?.selectedRequest?.case_id || props?.selectedInvoice?.invoice?.case_id);

  const defaultTrustId = useMemo(
    () =>
      caseAndLeadsList?.case_list?.[0]?.default_trust_account?.[0]?.account_id ??
      caseAndLeadsList?.lead_list?.[0]?.default_trust_account?.[0]?.account_id ??
      null,
    [caseAndLeadsList]
  );

  const trustAccount = useMemo(() => {
    let filteredAccounts = accountsList?.filter((a) => a?.account_type === 'trust');
    if (defaultTrustId) {
      filteredAccounts = filteredAccounts?.filter((account) => account?.account_id === defaultTrustId);
    }
    return filteredAccounts || [];
  }, [accountsList, defaultTrustId, typeOfPayment]);

  const operatingAccounts = useMemo(() => {
    let filteredAccounts = accountsList?.filter((a) => a?.account_type === 'operating');
    const defaultOperatingId =
      caseAndLeadsList?.case_list?.[0]?.default_operating_account?.[0]?.account_id ??
      caseAndLeadsList?.lead_list?.[0]?.default_operating_account?.[0]?.account_id;
    if (defaultOperatingId) {
      filteredAccounts = filteredAccounts?.filter((account) => account?.account_id === defaultOperatingId);
    }
    return filteredAccounts || [];
  }, [accountsList, caseAndLeadsList]);

  const allocationTrustAccounts = useMemo(() => accountsList?.filter((a) => a?.account_type === 'trust') || [], [accountsList]);

  const depositAccountList = useMemo(() => {
    return typeOfPayment === 'offline' ? [...trustAccount, ...operatingAccounts] : operatingAccounts;
  }, [typeOfPayment, trustAccount, operatingAccounts]);

  const entityDepositAccountId = useMemo(
    () =>
      selectedAccount?.account_id ??
      props.selectedRequest?.deposit_account_id ??
      props.selectedInvoice?.deposit_account_id ??
      invoiceDetails?.deposit_account_id,
    [selectedAccount, props.selectedRequest, props.selectedInvoice, invoiceDetails]
  );

  const isDepositLockedByCaseDefault = useMemo(
    () => isMappedViaCaseDefault(entityDepositAccountId, null, caseAndLeadsList),
    [entityDepositAccountId, caseAndLeadsList]
  );

  const isDepositTrustAccount = selectedAccount?.account_type === 'trust';
  const isTypeOfPaymentLockedToOffline = isDepositTrustAccount;

  const isFlatFeeInvoice = useMemo(() => {
    if (!props.selectedInvoice && !invoiceDetails) {
      return false;
    }
    const structure =
      invoiceDetails?.payment_structure ?? invoiceDetails?.invoice?.payment_structure ?? props.selectedInvoice?.payment_structure ?? null;
    return isFlatFeeStructure(structure);
  }, [props.selectedInvoice, invoiceDetails]);

  const isFlatFeePaymentRequest = useMemo(() => {
    if (!props.selectedRequest) {
      return false;
    }
    const structure =
      caseAndLeadsList?.case_list?.[0]?.billing_preferences?.payment_structure ??
      caseAndLeadsList?.lead_list?.[0]?.billing_preferences?.payment_structure ??
      null;
    return isFlatFeeStructure(structure);
  }, [props.selectedRequest, caseAndLeadsList]);

  const showMarkAsEarned = useMemo(
    () => isFlatFeeInvoice && typeOfPayment === 'offline' && selectedAccount?.account_type === 'operating',
    [isFlatFeeInvoice, typeOfPayment, selectedAccount]
  );

  const showAllocationFields = showMarkAsEarned && !markAsEarnedChecked;

  const resetAllocationState = useCallback(() => {
    setMarkAsEarnedChecked(false);
    setTransferToTrustAmount(null);
    setTemporaryFundAmount(null);
    setMarkAsEarnedAmount(null);
    setAllocationTrustAccount(undefined);
    setAllocationErrors({});
  }, []);

  useEffect(() => {
    totalCountEvent('account')
      .then((response) => {
        dispatch(listAccounts(response?.data));
      })
      .catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (props.selectedRequest?.case_id) {
      clearFormError();
      resetAllocationState();
      setSelectedPaymentMethod();
      setSelectedAccount();
      setDescription();
      setAmount();
    }
  }, [props.selectedRequest, resetAllocationState]);

  useEffect(() => {
    if (props.selectedInvoice && props.show) {
      const invoiceSk = props?.selectedInvoice?.sk;
      if (fetchedInvoiceSkRef.current === invoiceSk && invoiceDetails) {
        return;
      }

      clearFormError();
      resetAllocationState();
      setSelectedPaymentMethod();
      setSelectedAccount();
      setDescription();
      setAmount();

      if (invoiceSk) {
        fetchedInvoiceSkRef.current = invoiceSk;
        getInvoice(invoiceSk)
          .then((response) => {
            setInvoiceDetails({ ...response.data });
          })
          .catch((err) => {
            console.log('err', err);
            fetchedInvoiceSkRef.current = null;
          });
      }
    } else if (!props.show) {
      fetchedInvoiceSkRef.current = null;
      setInvoiceDetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.selectedInvoice?.sk, props.show]);

  useEffect(() => {
    if (!showAllocationFields) {
      return;
    }

    if (defaultTrustId) {
      const matchedDefaultTrust = allocationTrustAccounts.find((account) => account.account_id === defaultTrustId);
      if (matchedDefaultTrust) {
        setAllocationTrustAccount(matchedDefaultTrust);
        return;
      }
    }

    if (allocationTrustAccounts.length === 1) {
      setAllocationTrustAccount(allocationTrustAccounts[0]);
    }
  }, [showAllocationFields, defaultTrustId, allocationTrustAccounts]);

  useEffect(() => {
    if (isTypeOfPaymentLockedToOffline && typeOfPayment !== 'offline') {
      setTypeOfPayment('offline');
      setSelectedTrustAccount(undefined);
      setFormErrors((prev) => ({ ...prev, trust_account: '' }));
    }
  }, [isTypeOfPaymentLockedToOffline, typeOfPayment]);

  const onChangePayFull = (e) => {
    const checked = e.target.checked;
    setPayFull(checked);
    if (checked) {
      if (props.selectedRequest?.due_amount) {
        setAmount(props.selectedRequest?.due_amount);
      } else if (props.selectedInvoice?.due_amount) {
        setAmount(props.selectedInvoice?.due_amount);
      } else if (invoiceDetails?.due_amount != null) {
        setAmount(invoiceDetails.due_amount);
      }
    } else {
      setAmount(undefined);
    }
    setFormErrors((prev) => ({ ...prev, amount: '' }));
  };

  useEffect(() => {
    if (selectedTrustAccount?.account_id) {
      loadTrustAccountBalance(
        selectedTrustAccount?.account_id,
        props.selectedRequest?.case_id
          ? props.selectedRequest?.case_id
          : props.selectedInvoice?.case_id
          ? props.selectedInvoice?.case_id
          : props.selectedInvoice?.invoice?.case_id
      );
    }
  }, [selectedTrustAccount]);

  useEffect(() => {
    if (depositAccountList && depositAccountList.length > 0) {
      const acc = depositAccountList?.find(
        (a) =>
          (a?.account_id === props.selectedRequest?.deposit_account_id && props.selectedRequest?.deposit_account_id) ||
          (a?.account_id === props.selectedInvoice?.deposit_account_id && props.selectedInvoice?.deposit_account_id) ||
          (a?.account_id === invoiceDetails?.deposit_account_id && invoiceDetails?.deposit_account_id)
      );
      if (acc) {
        setSelectedAccount(acc);
      }
    }
  }, [depositAccountList, props.selectedRequest, props.selectedInvoice, invoiceDetails]);

  const validateAllocation = () => {
    if (!showMarkAsEarned) {
      setAllocationErrors({});
      return true;
    }

    if (markAsEarnedChecked) {
      setAllocationErrors({});
      return true;
    }

    const errors = {};
    const paymentAmountCents = toCents(amount);

    if (!allocationTrustAccount?.account_id) {
      errors.trust_account = 'Required';
    }

    const trustAmountCents = allocationAmountCents(transferToTrustAmount);
    const temporaryFundsCents = allocationAmountCents(temporaryFundAmount);
    const earnedAmountCents = allocationAmountCents(markAsEarnedAmount);

    if (transferToTrustAmount != null && transferToTrustAmount !== '' && trustAmountCents < 0) {
      errors.trust_amount = 'Must be zero or greater';
    }
    if (temporaryFundAmount != null && temporaryFundAmount !== '' && temporaryFundsCents < 0) {
      errors.temporary_funds_amount = 'Must be zero or greater';
    }
    if (markAsEarnedAmount != null && markAsEarnedAmount !== '' && earnedAmountCents < 0) {
      errors.earned_amount = 'Must be zero or greater';
    }

    if (Object.keys(errors).length > 0) {
      setAllocationErrors(errors);
      return false;
    }

    const allocationSumCents = trustAmountCents + temporaryFundsCents + earnedAmountCents;

    if (paymentAmountCents !== allocationSumCents) {
      setAllocationErrors({ allocation_sum: 'Allocation amounts must equal the payment amount' });
      return false;
    }

    setAllocationErrors({});
    return true;
  };

  const buildAllocationPayload = () => {
    if (!showMarkAsEarned) {
      return {};
    }

    if (markAsEarnedChecked) {
      return {
        mark_as_earned: true,
        allocation: {
          trust_amount: 0,
          trust_account_id: null,
          temporary_funds_amount: 0,
          earned_amount: Number(amount) || 0,
        },
      };
    }

    return {
      mark_as_earned: false,
      allocation: {
        trust_amount: Number(transferToTrustAmount) || 0,
        trust_account_id: allocationTrustAccount?.account_id ?? null,
        temporary_funds_amount: Number(temporaryFundAmount) || 0,
        earned_amount: Number(markAsEarnedAmount) || 0,
      },
    };
  };

  const validateForm = () => {
    let due_amount = props.selectedRequest?.due_amount;
    if (props.selectedInvoice && invoiceDetails) {
      due_amount = invoiceDetails.due_amount;
    }
    due_amount = due_amount ? parseFloat(due_amount) : 0;
    let isValid = true;
    const nextErrors = { ...formErrors };

    if (!amount || amount <= 0) {
      isValid = false;
      nextErrors.amount = 'Required a valid amount';
    }
    if (amount && amount > due_amount) {
      isValid = false;
      nextErrors.amount = "Amount can't be higher than the due amount";
    }
    if (!recordDate) {
      isValid = false;
      nextErrors.record_date = 'Required';
    }
    const isValidDepositAccount = selectedAccount && depositAccountList.some((acc) => acc.account_id === selectedAccount.account_id);
    if (!isValidDepositAccount) {
      isValid = false;
      nextErrors.deposit_info = 'Required';
    }
    if (typeOfPayment === 'offline' && !selectedPaymentMethod) {
      isValid = false;
      nextErrors.payment_method = 'Required';
    }
    if (typeOfPayment === 'trust' && amount && amount > trustAccountBalance) {
      isValid = false;
      nextErrors.amount = "Amount can't be higher than the trust account balance";
    }
    if (typeOfPayment === 'trust' && !selectedTrustAccount) {
      isValid = false;
      nextErrors.trust_account = 'Required';
    }
    if (!validateAllocation()) {
      isValid = false;
    }

    setFormErrors(nextErrors);
    return isValid;
  };

  const onSubmit = () => {
    if (validateForm()) {
      if (!props.isEdit) {
        setLoading(true);
        let sk = props.selectedRequest?.sk;
        let payment_request_number = props.selectedRequest?.payment_request_number;
        let due_amount = props.selectedRequest?.due_amount;
        let case_id = props.selectedRequest?.case_id;
        let case_name = props.selectedRequest?.case_name;
        if (props.selectedInvoice && invoiceDetails) {
          sk = invoiceDetails?.sk;
          payment_request_number = invoiceDetails?.invoice?.invoice_number;
          due_amount = invoiceDetails.due_amount;
          case_id = invoiceDetails?.invoice?.case_id;
          case_name = invoiceDetails?.invoice?.case_name;
        }
        let request_id = sk.split('#').reverse()[0];

        let data = {
          case_id: case_id,
          case_name: case_name,
          billing_contact_id: props?.selectedRequest?.billing_contact_id
            ? props?.selectedRequest?.billing_contact_id
            : props?.selectedInvoice?.invoice?.contact_id,
          billing_contact_name: props?.selectedRequest?.billing_contact_name
            ? props?.selectedRequest?.billing_contact_name
            : props?.selectedInvoice?.invoice?.contact_name,
          description: description,
          deposit_into: selectedAccount?.account_type,
          deposit_account_name: selectedAccount?.account_name,
          paid_amount: props.selectedRequest?.paid_amount ? props.selectedRequest?.paid_amount : props?.selectedInvoice?.paid_amount,
          payment_record_amount: amount,
          due_amount: due_amount,
          amount: invoiceDetails?.due_amount ? invoiceDetails?.due_amount : props.selectedRequest?.amount,
          payment_record_date: recordDate ? recordDate : '',
          payment_method: selectedPaymentMethod,
          payment_ref_id: request_id,
          payment_ref_number: payment_request_number,
          payment_record_status: amount === due_amount ? 'FULL' : 'PARTIAL',
          payment_record_type: typeOfPayment === 'offline' ? 'OFFLINE' : 'TRUST',
          payment_for: 'payment_request',
          payment_ref_qb_id: props?.selectedInvoice?.qb_id ? props?.selectedInvoice?.qb_id : props?.selectedRequest?.qb_id,
        };
        data.deposit_account_id = selectedAccount?.account_id;
        if (typeOfPayment === 'trust') {
          data.debit_account_name = selectedTrustAccount?.account_name;
          data.debit_account_id = selectedTrustAccount?.account_id;
        }
        Object.assign(data, buildAllocationPayload());
        let request_sk = props.selectedRequest?.sk;
        if (props.selectedInvoice && invoiceDetails) {
          request_sk = props.selectedInvoice?.sk;
          data.payment_for = 'invoice';
        }
        createPaymentRecord(
          data,
          request_sk,
          isLeadSpecific
            ? isLeadSpecific
            : props?.selectedRequest?.is_lead
            ? props?.selectedRequest?.is_lead
            : props?.selectedInvoice?.is_lead
        )
          .then(() => {
            setLoading(false);
            addToast(
              false,
              toastConstant.toasterType.SUCCESS,
              toastConstant.api.SUCCESS,
              toastConstant.message.CREATE_PAYMENT_RECORD_SUCCESS
            );
            queryClient.invalidateQueries({ queryKey: queryKeys.paymentHistory.all });
            invalidateClientFlatFeeBillingQueries(queryClient);
            props.onSuccess();
          })
          .catch((err) => {
            console.log('err', err);
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CREATE_PAYMENT_RECORD_FAILED);
            setLoading(false);
          });
      }
    }
  };

  const clearFormError = () => {
    setFormErrors({
      contact: '',
      amount: '',
      payment_method: '',
      deposit_info: '',
      trust_account: '',
    });
  };

  const loadTrustAccountBalance = (trust_account_id, case_id) => {
    getTrustAccountBalance(trust_account_id, case_id)
      .then((response) => {
        let available_balance = response?.data?.available_balance;
        available_balance = available_balance ? parseFloat(available_balance) : '';
        setTrustAccountBalance(available_balance);
      })
      .catch(() => {});
  };

  const handleMarkAsEarnedChange = (checked) => {
    setMarkAsEarnedChecked(checked);
    if (checked) {
      setTransferToTrustAmount(null);
      setTemporaryFundAmount(null);
      setMarkAsEarnedAmount(null);
      setAllocationTrustAccount(undefined);
      setAllocationErrors({});
    }
  };

  return (
    <Dialog
      visible={Boolean(props?.show && (props?.selectedRequest || props?.selectedInvoice || invoiceDetails))}
      onHide={props.onHide}
      header={ModalHeader}
      className="create-msg-popup"
      closable={false}
    >
      <div>
        {(props?.selectedRequest || props?.selectedInvoice || invoiceDetails) && (
          <>
            <div className="row">
              <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                <div className="modal-heading F-size16 text-bold">{'Record Payment'}</div>
              </div>

              <div className="d-flex align-items-center py-2 flex-wrap mb-2">
                <div className="col-12 col-sm-4 p-medium">Case Name</div>
                <div className="col-12 col-sm-8">
                  {props.selectedRequest?.case_name}
                  {invoiceDetails?.invoice?.case_name ? invoiceDetails?.invoice?.case_name : ''}
                </div>
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap mb-2">
                <div className="col-12 col-sm-4 p-medium">Due Amount</div>
                <div className="col-12 col-sm-8">
                  {props.selectedRequest ? dollarFormat(props.selectedRequest?.due_amount) : ''}
                  {invoiceDetails?.due_amount ? dollarFormat(invoiceDetails?.due_amount) : ''}
                </div>
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-12 col-sm-4 p-medium">Type of Payment</div>
                <div className="col-12 col-sm-8">
                  <Dropdown
                    options={typeOfPayments}
                    value={typeOfPayment}
                    className="w-100"
                    disabled={isTypeOfPaymentLockedToOffline}
                    aria-label="Type of Payment"
                    onChange={(e) => {
                      const newPaymentType = e.value;
                      setTypeOfPayment(newPaymentType);
                      setFormErrors((prev) => ({ ...prev, payment_method: '' }));
                      setSelectedPaymentMethod();

                      if (newPaymentType === 'trust') {
                        setSelectedAccount(null);
                        setSelectedTrustAccount(undefined);
                        setFormErrors((prev) => ({ ...prev, deposit_info: '', trust_account: '' }));
                      } else if (newPaymentType === 'offline') {
                        setSelectedTrustAccount(undefined);
                        setFormErrors((prev) => ({ ...prev, trust_account: '' }));
                        if (!selectedAccount) {
                          const offlineDepositAccountList = [...trustAccount, ...operatingAccounts];
                          if (offlineDepositAccountList.length > 0) {
                            setSelectedAccount(offlineDepositAccountList[0]);
                            setFormErrors((prev) => ({ ...prev, deposit_info: '' }));
                          }
                        } else {
                          const offlineDepositAccountList = [...trustAccount, ...operatingAccounts];
                          const isAccountValid = offlineDepositAccountList.some((acc) => acc.account_id === selectedAccount.account_id);
                          if (!isAccountValid && offlineDepositAccountList.length > 0) {
                            setSelectedAccount(offlineDepositAccountList[0]);
                            setFormErrors((prev) => ({ ...prev, deposit_info: '' }));
                          }
                        }
                      }
                    }}
                    filter
                  />
                </div>
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-12 col-sm-4 p-medium">
                  Contact
                  <span className="text-danger">*</span>
                </div>
                <div className="col-12 col-sm-8">
                  {props?.selectedRequest?.billing_contact_name
                    ? props?.selectedRequest?.billing_contact_name
                    : props?.selectedInvoice?.invoice?.contact_name
                    ? props?.selectedInvoice?.invoice?.contact_name
                    : invoiceDetails?.invoice?.contact_name}
                </div>
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-12 col-sm-4 p-medium">
                  Amount
                  <span className="text-danger">*</span>
                </div>
                <div className="col-12 col-sm-8">
                  <div className="d-flex align-items-center">
                    <InputNumber
                      className="input-shadow"
                      value={amount}
                      onValueChange={(e) => {
                        setAmount(e.value);
                        setFormErrors((prev) => ({ ...prev, amount: '' }));
                      }}
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      disabled={payFull}
                      aria-label="Amount"
                    />
                    <div className="align-items-center ms-2 text-nowrap">
                      <label className="switch">
                        <input type="checkbox" checked={payFull} name="weekends" onChange={onChangePayFull} />
                        <span className="slider round"></span>
                      </label>
                      <span className="ms-2">Pay in full</span>
                    </div>
                  </div>
                  {formErrors?.amount && <span className="text-danger">{formErrors.amount}</span>}
                </div>
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-12 col-sm-4 p-medium">
                  Date
                  <span className="text-danger">*</span>
                </div>
                <div className="col-12 col-sm-8">
                  <TimezoneDatePicker
                    className="input-shadow w-100"
                    value={recordDate}
                    onChange={(value) => {
                      setRecordDate(value);
                      setFormErrors((prev) => ({ ...prev, record_date: '' }));
                    }}
                    monthNavigator
                    yearNavigator
                    yearRange="1920:2070"
                    timezone={userContext?.userDetails?.timezone}
                    isEndOfDay={true}
                  />
                  {formErrors?.record_date && <span className="text-danger">{formErrors.record_date}</span>}
                </div>
              </div>
              {typeOfPayment === 'offline' && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-12 col-sm-4 p-medium">
                    Payment Method
                    <span className="text-danger">*</span>
                  </div>
                  <div className="col-12 col-sm-8">
                    <Dropdown
                      className="input-shadow w-100 p-0"
                      options={paymentMethods}
                      value={selectedPaymentMethod}
                      onChange={(e) => {
                        setSelectedPaymentMethod(e.value);
                        setFormErrors((prev) => ({ ...prev, payment_method: '' }));
                      }}
                      filter
                    />
                    {formErrors?.payment_method && <span className="text-danger">{formErrors.payment_method}</span>}
                  </div>
                </div>
              )}

              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-12 col-sm-4 p-medium">
                  Deposit Info
                  <span className="text-danger">*</span>
                </div>
                <div className="col-12 col-sm-8">
                  <Dropdown
                    className="input-shadow w-100 p-0"
                    options={depositAccountList}
                    value={selectedAccount}
                    onChange={(e) => {
                      setSelectedAccount(e.value);
                      setFormErrors((prev) => ({ ...prev, deposit_info: '' }));
                    }}
                    optionLabel={'account_name'}
                    filter
                    disabled={isDepositLockedByCaseDefault}
                    aria-label="Deposit Info"
                  />
                  {formErrors?.deposit_info && <span className="text-danger">{formErrors.deposit_info}</span>}
                </div>
              </div>

              {typeOfPayment === 'trust' && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-12 col-sm-4 p-medium">
                    Trust
                    <span className="text-danger">*</span>
                  </div>
                  <div className="col-12 col-sm-8">
                    <Dropdown
                      className="input-shadow w-100 p-0"
                      options={trustAccount}
                      value={selectedTrustAccount}
                      onChange={(e) => {
                        setSelectedTrustAccount(e.value);
                        setFormErrors((prev) => ({ ...prev, trust_account: '' }));
                      }}
                      optionLabel={'account_name'}
                      filter
                      placeholder="Select"
                    />
                    {formErrors?.trust_account && <span className="text-danger">{formErrors.trust_account}</span>}
                  </div>
                </div>
              )}

              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-12 col-sm-4 p-medium d-flex align-items-center flex-wrap gap-1">
                  <span>Description</span>
                  <i
                    className="pi pi-info-circle text-muted cursor-pointer record-payment-description-tip flex-shrink-0 align-middle"
                    style={{ fontSize: '12px', lineHeight: 1, padding: '5px' }}
                    aria-label="More information"
                    role="img"
                  />
                </div>
                <div className="col-12 col-sm-8">
                  <TextSnippetWrapper
                    type="textarea"
                    className="input-shadow w-100 p-2"
                    value={description}
                    onChange={(e) => {
                      if (e?.target?.value?.length <= 255) {
                        setDescription(e?.target?.value);
                      }
                    }}
                    maxLength={255}
                    autoComplete="off"
                  />
                  <small className="text-muted d-block text-end">{description?.length || 0}/255</small>
                </div>
              </div>
              <Tooltip
                target=".record-payment-description-tip"
                content="This note will be visible to the client."
                position="top"
                showDelay={300}
              />

              {!isFlatFeePaymentRequest && (
                <RecordPaymentEarnedAllocationFields
                  showMarkAsEarned={showMarkAsEarned}
                  showAllocationFields={showAllocationFields}
                  markAsEarnedChecked={markAsEarnedChecked}
                  onMarkAsEarnedChange={handleMarkAsEarnedChange}
                  allocationTrustAccounts={allocationTrustAccounts}
                  selectedAllocationTrustAccount={allocationTrustAccount}
                  onAllocationTrustChange={(value) => {
                    setAllocationTrustAccount(value);
                    setAllocationErrors((prev) => ({ ...prev, trust_account: undefined }));
                  }}
                  disableAllocationTrustSelection={Boolean(defaultTrustId)}
                  transferToTrustAmount={transferToTrustAmount}
                  onTransferToTrustAmountChange={(value) => {
                    setTransferToTrustAmount(value);
                    setAllocationErrors((prev) => ({ ...prev, trust_amount: undefined, allocation_sum: undefined }));
                  }}
                  temporaryFundAmount={temporaryFundAmount}
                  onTemporaryFundAmountChange={(value) => {
                    setTemporaryFundAmount(value);
                    setAllocationErrors((prev) => ({ ...prev, temporary_funds_amount: undefined, allocation_sum: undefined }));
                  }}
                  markAsEarnedAmount={markAsEarnedAmount}
                  onMarkAsEarnedAmountChange={(value) => {
                    setMarkAsEarnedAmount(value);
                    setAllocationErrors((prev) => ({ ...prev, earned_amount: undefined, allocation_sum: undefined }));
                  }}
                  allocationErrors={allocationErrors}
                />
              )}
            </div>

            <div className="footer-btn">
              <div className="col-12 d-flex justify-content-end">
                <Button
                  className="p-button-secondary outline mx-2"
                  label="Cancel"
                  onClick={() => {
                    props?.onHide();
                  }}
                />
                <Button
                  className="p-button-secondary"
                  label={props?.selectedExpense ? 'Update' : 'Save'}
                  onClick={onSubmit}
                  loading={loading}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
